/**
 * Our Offensive Vocab — standalone learning module rendered when
 * nav === "vocab". Brand-matched to ContentPage.jsx exactly so it
 * sits inside the platform without standing out.
 *
 * Title style, tab pill style, search bar style, filter chip style,
 * group label style all lifted directly from ContentPage so the
 * vocab page reads as a peer of Drills & Games / Practice Plans /
 * etc. Not a designer template.
 *
 * Top-level tabs mirror the PDF (Offense / Defense / Misc). Offense
 * is a long-form scroll with the 9 layers stacked as sub-sections;
 * a sticky right-rail nav lets coaches jump between layers.
 * Defense is a placeholder. Misc shows the cross-cutting terms.
 * Term clicks open a slide-in drawer for the full detail.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, BookOpen, ChevronRight, X, Plus } from "lucide-react";
import { GOLD } from "../constants/theme";
import { Ic } from "../constants/icons";
import { ADAPT_GROUPS, ADAPT_CONCEPTS, ADAPT_SECTIONS, searchConcepts } from "./adaptConcepts";

export function VocabPage({ C, dark }) {
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [tab, setTab] = useState("offense");
  const [activeTerm, setActiveTerm] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);

  const isSearching = query.trim().length > 0;
  const searchResults = useMemo(() => isSearching ? searchConcepts(query) : [], [isSearching, query]);

  return (
    <div style={{ flex: 1, overflow: "auto", background: C.bg, color: C.text }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px 80px" }}>
        {/* Back to X & O */}
        <button onClick={() => { window.location.hash = "#/xo"; }}
          style={{ background: "transparent", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: C.textMid, fontSize: 13, fontWeight: 700, padding: "4px 8px 4px 0", marginBottom: 14 }}>
          <ArrowLeft size={13}/> X &amp; O
        </button>

        {/* Title — matches ContentPage exactly: fontSize 20, fontWeight 800, uppercase */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <BookOpen size={18} color={GOLD}/>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: 0.5 }}>Our Offensive Vocab</div>
        </div>
        <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.5, marginBottom: 12 }}>
          The vocabulary every BAM coach needs to fluently teach the system.
        </div>

        {/* A coach who reads this and finds their defense unnamed should know
            that's a gap we're filling, not a judgment that defense doesn't get
            a language. Saying so also gives the notification a reason to
            exist: nobody opts into a page, they opt into what comes next. */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20,
          padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`,
          background: "rgba(255,255,255,0.02)", maxWidth: 720 }}>
          <Plus size={15} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }}/>
          <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6 }}>
            <strong style={{ color: C.text, fontWeight: 700 }}>This is the first batch.</strong>{" "}
            Defense and the rest of the misc terms are being written now, and the
            offensive layers keep growing. New terms land in batches, and we'll
            tell you when one does. Turn on{" "}
            <button className="btn-inline" onClick={() => { window.location.hash = "#/settings"; }}
              style={{ background: "transparent", border: "none", padding: 0, color: GOLD,
                fontFamily: "inherit", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
              notifications
            </button>{" "}
            if you want to hear about it.
          </div>
        </div>

        {/* Section tabs — matches ContentPage tab pattern: gold-tint background when active */}
        {!isSearching && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
            {ADAPT_SECTIONS.map(s => {
              const on = tab === s.id;
              return (
                <button key={s.id} onClick={() => setTab(s.id)}
                  style={{ padding: "8px 18px", borderRadius: 9, fontSize: 12, fontWeight: 800, letterSpacing: 0.3, cursor: "pointer",
                    border: `1px solid ${on ? GOLD : C.border}`,
                    background: on ? `${GOLD}18` : "transparent",
                    color: on ? GOLD : C.textMid, transition: "all .15s", fontFamily: "inherit" }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Search bar — matches ContentPage search exactly */}
        <div style={{ position: "relative", marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, background: dark ? "rgba(255,255,255,0.03)" : C.bgHover, border: `1px solid ${searchFocused ? GOLD + "50" : C.cardBorder || C.border}`, borderRadius: 10, padding: "8px 14px", transition: "border-color .15s ease" }}>
            <Ic.Search c={GOLD} s={14}/>
            <input value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              placeholder="Search the vocab..."
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 13, color: C.text, fontFamily: "'DM Sans',sans-serif" }}/>
            {query && <span onClick={() => setQuery("")} style={{ color: C.textDim, fontSize: 14, lineHeight: 1, cursor: "pointer" }}>×</span>}
          </div>
        </div>

        {/* Body */}
        {isSearching ? (
          <SearchView C={C} query={query} results={searchResults} onPickTerm={setActiveTerm}/>
        ) : tab === "offense" ? (
          <OffenseView C={C} onPickTerm={setActiveTerm} activeGroup={activeGroup} setActiveGroup={setActiveGroup}/>
        ) : tab === "defense" ? (
          <DefenseView C={C}/>
        ) : (
          <MiscView C={C} onPickTerm={setActiveTerm}/>
        )}
      </div>

      {/* Term detail drawer */}
      {activeTerm && <TermDrawer term={activeTerm} onClose={() => setActiveTerm(null)} onPickTerm={setActiveTerm} C={C} dark={dark}/>}
    </div>
  );
}

// ── Offense view: stacked layer sections ────────────────────────────

function OffenseView({ C, onPickTerm, activeGroup, setActiveGroup }) {
  const offenseGroups = ADAPT_GROUPS.filter(g => g.section === "offense");
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveGroup(visible[0].target.dataset.group);
      },
      { rootMargin: "-25% 0px -55% 0px", threshold: [0, 0.25, 0.5] }
    );
    Object.values(sectionRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [setActiveGroup]);

  const jumpTo = (gid) => {
    const el = sectionRefs.current[gid];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 200px", gap: 32 }}>
      <div>
        {/* Layer sections, stacked */}
        {offenseGroups.map(g => (
          <LayerSection key={g.id} group={g} onPickTerm={onPickTerm} C={C}
            innerRef={el => { sectionRefs.current[g.id] = el; }}/>
        ))}
      </div>

      {/* Sticky right-rail nav */}
      <aside style={{
        position: "sticky", top: 24, alignSelf: "start",
        padding: "12px 12px 14px", borderRadius: 10,
        border: `1px solid ${C.border}`,
        background: "rgba(255,255,255,0.015)",
        maxHeight: "calc(100vh - 120px)",
        overflow: "auto",
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, paddingLeft: 4 }}>Layers</div>
        {offenseGroups.map((g, i) => {
          const isActive = activeGroup === g.id;
          return (
            <button key={g.id} onClick={() => jumpTo(g.id)} type="button"
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "5px 8px", fontSize: 11.5, fontWeight: isActive ? 800 : 600,
                background: isActive ? `${GOLD}18` : "transparent",
                color: isActive ? GOLD : C.textMid,
                border: "none", cursor: "pointer", borderRadius: 6,
                fontFamily: "inherit", textAlign: "left", lineHeight: 1.3,
                marginBottom: 1,
                transition: "background .12s, color .12s",
              }}>
              <span style={{ fontVariantNumeric: "tabular-nums", color: isActive ? GOLD : C.textDim, fontWeight: 700, fontSize: 9.5, minWidth: 14 }}>0{i + 1}</span>
              <span style={{ flex: 1 }}>{g.label}</span>
            </button>
          );
        })}
      </aside>
    </div>
  );
}

// ── A single layer section ──────────────────────────────────────────

function LayerSection({ group, onPickTerm, C, innerRef }) {
  const terms = ADAPT_CONCEPTS.filter(c => c.group === group.id && !c.parent);
  const hasLevels = terms.some(t => t.level);
  const byLevel = hasLevels
    ? {
        basic:        terms.filter(t => t.level === "basic"),
        intermediate: terms.filter(t => t.level === "intermediate"),
        advanced:     terms.filter(t => t.level === "advanced"),
      }
    : null;

  return (
    <section ref={innerRef} data-group={group.id}
      id={`vocab-${group.id}`}
      style={{ paddingTop: 26, paddingBottom: 26, borderTop: `1px solid ${C.border}`, scrollMarginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.textDim, letterSpacing: 0.8, textTransform: "uppercase", fontVariantNumeric: "tabular-nums" }}>0{group.order}</span>
        <span style={{ fontSize: 16, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: 0.5 }}>{group.label}</span>
      </div>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 12, lineHeight: 1.4 }}>{group.blurb}</div>
      <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, marginBottom: 18, maxWidth: 720 }}>{group.intro}</div>

      {hasLevels ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[
            { id: "basic", label: "Basic" },
            { id: "intermediate", label: "Intermediate" },
            { id: "advanced", label: "Advanced" },
          ].filter(lev => byLevel[lev.id].length > 0).map(lev => (
            <div key={lev.id}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 7 }}>{lev.label}</div>
              <TermGrid terms={byLevel[lev.id]} onPickTerm={onPickTerm} C={C}/>
            </div>
          ))}
        </div>
      ) : (
        <TermGrid terms={terms} onPickTerm={onPickTerm} C={C}/>
      )}
    </section>
  );
}

function TermGrid({ terms, onPickTerm, C }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
      {terms.map(t => <TermCard key={t.id} term={t} onPick={() => onPickTerm(t)} C={C}/>)}
    </div>
  );
}

function TermCard({ term, onPick, C }) {
  const [hover, setHover] = useState(false);
  const subs = ADAPT_CONCEPTS.filter(c => c.parent === term.id);
  return (
    <button onClick={onPick} type="button"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        textAlign: "left", padding: "11px 13px", borderRadius: 10,
        background: hover ? (C.cardGradient || `${GOLD}10`) : "rgba(255,255,255,0.02)",
        border: `1px solid ${hover ? `${GOLD}55` : C.border}`,
        cursor: "pointer", fontFamily: "inherit", color: C.text,
        transition: "background .12s, border-color .12s, transform .12s",
        transform: hover ? "translateY(-1px)" : "none",
        display: "flex", flexDirection: "column", gap: 5,
      }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>{term.term}</span>
        {subs.length > 0 && (
          <span style={{ fontSize: 9, color: C.textDim, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>+{subs.length} variant{subs.length === 1 ? "" : "s"}</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {term.shortDef}
      </div>
      {term.tags && term.tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
          {term.tags.slice(0, 3).map(t => (
            <span key={t} style={{ fontSize: 9, fontWeight: 700, color: GOLD, padding: "1px 7px", borderRadius: 4, background: `${GOLD}10`, border: `1px solid ${GOLD}30`, letterSpacing: 0.3, textTransform: "uppercase" }}>{t}</span>
          ))}
        </div>
      )}
    </button>
  );
}

// ── Defense placeholder ────────────────────────────────────────────

function DefenseView({ C }) {
  return (
    <div style={{ padding: "60px 30px", textAlign: "center", borderRadius: 12, border: `1px dashed ${C.border}`, background: "rgba(255,255,255,0.015)" }}>
 <div style={{ fontSize: 14, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Defense Vocab: In Progress</div>
 <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.55, maxWidth: 460, margin: "0 auto" }}>The defensive vocabulary will land here as it's drafted. Same pattern as Offense: layered, taught in order, with the same kind of definitions and tag system.</div>
    </div>
  );
}

// ── Misc ────────────────────────────────────────────────────────────

function MiscView({ C, onPickTerm }) {
  const miscGroup = ADAPT_GROUPS.find(g => g.id === "misc");
  const terms = ADAPT_CONCEPTS.filter(c => c.group === "misc" && !c.parent);
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{miscGroup?.label}</div>
        <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.55, maxWidth: 640 }}>{miscGroup?.intro}</div>
      </div>
      <TermGrid terms={terms} onPickTerm={onPickTerm} C={C}/>
    </div>
  );
}

// ── Search results ─────────────────────────────────────────────────

function SearchView({ C, query, results, onPickTerm }) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.textMid }}>
          {results.length} {results.length === 1 ? "match" : "matches"} for <strong style={{ color: C.text }}>"{query}"</strong>
        </div>
      </div>
      {results.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: C.textDim, fontSize: 13 }}>
          No matches. Try a different keyword.
        </div>
      ) : (
        <TermGrid terms={results} onPickTerm={onPickTerm} C={C}/>
      )}
    </div>
  );
}

// ── Slide-in drawer for term detail ────────────────────────────────

function TermDrawer({ term, onClose, onPickTerm, C, dark }) {
  const group = ADAPT_GROUPS.find(g => g.id === term.group);
  const parent = term.parent ? ADAPT_CONCEPTS.find(c => c.id === term.parent) : null;
  const subs = ADAPT_CONCEPTS.filter(c => c.parent === term.id);
  const groupSiblings = ADAPT_CONCEPTS.filter(c =>
    c.group === term.group && c.id !== term.id && !c.parent && (!parent || c.id !== parent.id)
  ).slice(0, 8);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(2px)", zIndex: 100 }}/>
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(560px, 100%)",
        background: C.bg, borderLeft: `1px solid ${C.border}`, zIndex: 101,
        display: "flex", flexDirection: "column", animation: "drawerSlide .22s cubic-bezier(.2,.8,.2,1)",
        boxShadow: "-24px 0 60px rgba(0,0,0,0.5)",
      }}>
        <style>{`@keyframes drawerSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: GOLD, letterSpacing: 1.4, textTransform: "uppercase" }}>{group?.label}</span>
              {parent && (
                <>
                  <ChevronRight size={11} color={C.textDim}/>
                  <button className="btn-inline" onClick={() => onPickTerm(parent)} style={{ background: "transparent", border: "none", cursor: "pointer", color: C.textMid, fontSize: 11, fontWeight: 600, padding: 0, fontFamily: "inherit" }}>{parent.term}</button>
                </>
              )}
              {term.level && (
                <span style={{ fontSize: 9.5, fontWeight: 700, color: C.textDim, letterSpacing: 0.6, textTransform: "uppercase", padding: "1px 7px", borderRadius: 4, background: `${C.border}88` }}>{term.level}</span>
              )}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: C.textMid, padding: 6, borderRadius: 6, display: "flex", alignItems: "center" }}>
            <X size={16}/>
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "24px 24px 40px" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>{term.term}</div>
          <div style={{ fontSize: 15, color: C.text, lineHeight: 1.55, fontWeight: 500, marginBottom: 14 }}>{term.shortDef}</div>
          {term.longDef && (
            <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.65, whiteSpace: "pre-wrap", marginBottom: 16 }}>{term.longDef}</div>
          )}
          {term.tags && term.tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 22 }}>
              {term.tags.map(t => (
                <span key={t} style={{ fontSize: 10, fontWeight: 700, color: GOLD, padding: "3px 9px", borderRadius: 5, background: `${GOLD}10`, border: `1px solid ${GOLD}40`, letterSpacing: 0.4, textTransform: "uppercase" }}>{t}</span>
              ))}
            </div>
          )}

          {subs.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Variants</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {subs.map(s => <TermCard key={s.id} term={s} onPick={() => onPickTerm(s)} C={C}/>)}
              </div>
            </div>
          )}

          {groupSiblings.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>More in {group?.label}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {groupSiblings.map(s => (
                  <button key={s.id} onClick={() => onPickTerm(s)} type="button"
                    style={{ padding: "5px 14px", fontSize: 11.5, fontWeight: 700, borderRadius: 20, background: "transparent", color: C.textMid, border: `1px solid ${C.border}`, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all .12s" }}>
                    {s.term}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 9, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", border: `1px dashed ${C.border}` }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Coming Soon</div>
            <div style={{ fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>Court diagram, video example, and a list of drills using this concept.</div>
          </div>
        </div>
      </aside>
    </>
  );
}

export default VocabPage;
